<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ editingBrand ? "Edit Brand" : "Add New Brand" }}</h2>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="submitForm">
        <div class="form-grid">
          <!-- Brand Name -->
          <div class="form-group">
            <label>Brand Name*</label>
            <input
              type="text"
              v-model="formData.name"
              required
              placeholder="Enter brand name"
            />
          </div>

          <!-- Category Dropdown -->
          <div class="form-group">
            <label>Select Category*</label>
            <select v-model="formData.category_id">
              <option value="" disabled>Select a category</option>
              <option v-for="category in categoryData" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="closeModal" class="btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn-primary" :disabled="isSubmitting">
            {{
              isSubmitting ? "Processing..." : editingBrand ? "Update" : "Save"
            }}
            Brand
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  show: Boolean,
  editingCategory: Object,
  initialFormData: Object,
  categoryData: Array, // Accept real category data
});

const emit = defineEmits(["close", "submit"]);

// Initialize form data with default values
const formData = ref({
  name: props.initialFormData?.name || "",
  product_categories_id: props.initialFormData?.category_id || "",
});

// Close modal function
const closeModal = () => {
  emit("close");
};

// Prepare form data for submission
const prepareFormData = () => {
  return {
    name: formData.value.name,
    product_categories_id: formData.value.category_id,
  };
};

// Handle form submission
const submitForm = () => {
  const formPayload = prepareFormData();
  emit("submit", formPayload);
};
</script>


<style scoped>
/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0.5rem;
}

.close-btn:hover {
  color: #2c3e50;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 0;
}

.form-group.full-width {
  grid-column: span 2;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.confirm-modal {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  text-align: center;
}

.confirm-modal h3 {
  margin-top: 0;
  color: #2c3e50;
}

.confirm-modal p {
  margin-bottom: 2rem;
  color: #7f8c8d;
}

.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

/* Image upload styles */
.image-upload {
  margin-top: 0.5rem;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 6px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 1rem;
}

.upload-area:hover {
  border-color: #3498db;
  background-color: #f8f9fa;
}

.upload-area i {
  font-size: 2rem;
  color: #7f8c8d;
  margin-bottom: 0.5rem;
}

.upload-area p {
  margin: 0;
  color: #7f8c8d;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 1rem;
}

.image-preview {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  display: block;
}
.file-input {
  display: none;
}

.upload-area {
  cursor: pointer;
  border: 2px dashed #ddd;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
}

.upload-area:hover {
  border-color: #3498db;
  background-color: #f8f9fa;
}

.error-message {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 5px;
}

.upload-info {
  font-size: 0.85rem;
  color: #666;
  margin-top: 5px;
}

.image-preview {
  position: relative;
  margin: 5px;
}

.image-preview img {
  max-width: 100px;
  max-height: 100px;
  object-fit: contain;
  border: 1px solid #eee;
}
.remove-image {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 24px;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.7);
  border: none;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-preview:hover .remove-image {
  opacity: 1;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover {
  background-color: #2980b9;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
}

.btn-danger:hover {
  background-color: #c0392b;
}

.btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #f8f9fa;
  color: #7f8c8d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  background-color: #e9ecef;
  color: #2c3e50;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: span 1;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .control-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .products-table th,
  .products-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  .actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .product-management {
    padding: 1rem;
  }

  .modal-content {
    width: 95%;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
  }
}
</style>

